import React, { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { PlanService } from "../../Services";
import Loader from "../../Components/Loader";
import FeatureRows, { packFeatureRows, seedFeatureRows } from "./FeatureRows";

export const PlanList = () => {
  const history = useHistory();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loader, setLoader] = useState(true);
  const [activeTab, setActiveTab] = useState(history.location.state?.tab || "plans");
  const [requests, setRequests] = useState([]);
  const [quote, setQuote] = useState(null);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab !== 'custom') return;
    PlanService.markQueryNotificationsRead().then(() => {
      window.dispatchEvent(new Event('plan-queries-read'));
    }).catch(() => {});
  }, [activeTab]);

  const handleDeleteQuote = (item) => {
    if (!window.confirm(`Delete the customization request for "${item.company_name || item.email}"?`)) return;
    setLoader(true);
    PlanService.deleteCustomQuote({ id: item._id })
      .then((res) => {
        if (res && res.status) {
          if (quote && quote.id === item._id) setQuote(null);
          if (viewing && viewing._id === item._id) setViewing(null);
          fetchData();
        } else {
          window.alert(res?.message || "Failed to delete the request");
          setLoader(false);
        }
      })
      .catch(() => {
        window.alert("Failed to delete the request");
        setLoader(false);
      });
  };

  const handleDeletePlan = (item) => {
    if (!window.confirm(`Delete plan "${item.name}"?`)) return;
    setLoader(true);
    PlanService.deletePlan({ id: item._id })
      .then((res) => {
        if (res && res.status) {
          fetchData();
        } else {
          window.alert(res?.message || "Failed to delete plan");
          setLoader(false);
        }
      })
      .catch(() => {
        window.alert("Failed to delete plan");
        setLoader(false);
      });
  };

  const handleDeleteAddon = (item) => {
    if (!window.confirm(`Delete addon "${item.name}"? It will also be removed from plans.`)) return;
    setLoader(true);
    PlanService.deleteAddon({ id: item._id })
      .then((res) => {
        if (res && res.status) {
          fetchData();
        } else {
          window.alert(res?.message || "Failed to delete addon");
          setLoader(false);
        }
      })
      .catch(() => {
        window.alert("Failed to delete addon");
        setLoader(false);
      });
  };

  const fetchData = () => {
    setLoader(true);
    Promise.all([PlanService.getPlans(), PlanService.getAddons(), PlanService.getCustomPlans()])
      .then(([planRes, addonRes, customRes]) => {
        if (planRes.status) setPlans(planRes.data);
        if (addonRes.status) setAddons(addonRes.data);
        if (customRes.status) setRequests(customRes.data || []);
        setLoader(false);
      })
      .catch(err => {
        console.log(err);
        setLoader(false);
      });
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            
            {/* Nav tabs */}
            <ul className="nav nav-tabs nav-tabs-custom nav-justified mb-4" role="tablist">
              <li className="nav-item">
                <a className={`nav-link ${activeTab === 'plans' ? 'active' : ''}`} onClick={() => setActiveTab('plans')} style={{ cursor: 'pointer' }}>
                  <span className="d-block d-sm-none"><i className="fas fa-home"></i></span>
                  <span className="d-none d-sm-block fw-bold" style={{ fontSize: '1rem' }}>Subscription Plans</span>
                </a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${activeTab === 'addons' ? 'active' : ''}`} onClick={() => setActiveTab('addons')} style={{ cursor: 'pointer' }}>
                  <span className="d-block d-sm-none"><i className="far fa-user"></i></span>
                  <span className="d-none d-sm-block fw-bold" style={{ fontSize: '1rem' }}>Manage Addons</span>
                </a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${activeTab === 'custom' ? 'active' : ''}`} onClick={() => setActiveTab('custom')} style={{ cursor: 'pointer' }}>
                  <span className="d-none d-sm-block fw-bold" style={{ fontSize: '1rem' }}>
                    Customize Requests
                    {requests.some((item) => item.status === 'PENDING' && item.seen_by_admin !== true) && (
                      <span className="badge bg-danger rounded-pill ms-2">{requests.filter((item) => item.status === 'PENDING' && item.seen_by_admin !== true).length}</span>
                    )}
                  </span>
                </a>
              </li>
            </ul>

            {/* Tab content */}
            <div className="tab-content text-muted">
              
              {/* Plans Tab */}
              {activeTab === 'plans' && (
                <div className="tab-pane active" role="tabpanel">
                  <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary w-sm waves-effect waves-light" onClick={() => history.push("/add-plan")}>
                      Add Plan
                    </button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-centered table-nowrap mb-0 table-bordered text-center">
                      <thead className="table-light">
                        <tr>
                          <th>Plan Name</th>
                          <th>Type</th>
                          <th>Monthly Price</th>
                          <th>Yearly Price</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plans && plans.length > 0 ? (
                          plans.map((item, index) => (
                            <tr key={index}>
                              <td>{item.name}</td>
                              <td>{item.type_name || '-'}</td>
                              <td>${item.monthly_price || 0}</td>
                              <td>${item.yearly_price || 0}</td>
                              <td><span className={`badge bg-${item.status === 'ACTIVE' ? 'success' : 'danger'}`}>{item.status}</span></td>
                              <td>
                                <button className="btn btn-info btn-sm me-2" onClick={() => history.push("/edit-plan?id=" + item._id, item)}>
                                  Edit
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={() => handleDeletePlan(item)}>
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="6">No plans found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Addons Tab */}
              {activeTab === 'addons' && (
                <div className="tab-pane active" role="tabpanel">
                  <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-success w-sm waves-effect waves-light" onClick={() => history.push("/add-addon")}>
                      Add Addon
                    </button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-centered table-nowrap mb-0 table-bordered text-center">
                      <thead className="table-light">
                        <tr>
                          <th>Addon Name</th>
                          <th>Type</th>
                          <th>Price</th>
                          <th>Duration</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addons && addons.length > 0 ? (
                          addons.map((item, index) => (
                            <tr key={index}>
                              <td>{item.name}</td>
                              <td>{item.type_name || '-'}</td>
                              <td>${item.price}</td>
                              <td>{item.duration}</td>
                              <td><span className={`badge bg-${item.status === 'ACTIVE' ? 'success' : 'danger'}`}>{item.status}</span></td>
                              <td>
                                <button className="btn btn-info btn-sm me-2" onClick={() => history.push("/edit-addon?id=" + item._id, item)}>
                                  Edit
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={() => handleDeleteAddon(item)}>
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="6">No addons found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'custom' && (
                <div className="tab-pane active">
                  <div className="table-responsive mb-4">
                    <table className="table table-centered table-nowrap mb-0 table-bordered text-center">
                      <thead className="table-light">
                        <tr>
                          <th>Company</th>
                          <th>Email</th>
                          <th>Asked for</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.length > 0 ? requests.map((item) => (
                          <tr key={item._id}>
                            <td>{item.company_name}{item.status === 'PENDING' && item.seen_by_admin !== true ? <span className="badge bg-danger ms-2">New</span> : null}</td>
                            <td>{item.email}</td>
                            <td>{item.question || item.message || `${item.requested_credits || 0} credits · ${item.requested_users || item.max_users || 0} users`}{item.monthly_price ? ` · $${item.monthly_price}/mo` : ''}</td>
                            <td><span className={`badge bg-${item.status === 'PAID' ? 'success' : item.status === 'QUOTED' ? 'info' : item.status === 'REJECTED' ? 'danger' : 'warning'}`}>{item.status}</span></td>
                            <td>
                              <button className="btn btn-outline-primary btn-sm me-2" onClick={() => { setQuote(null); setViewing(item); }}>View</button>
                              <button className="btn btn-outline-danger btn-sm me-2" onClick={() => handleDeleteQuote(item)}>Delete</button>
                              {item.status !== 'PAID' && (
                                <button className="btn btn-info btn-sm" onClick={() => { setViewing(null); setQuote({
                                  id: item._id,
                                  company_name: item.company_name,
                                  message: item.message,
                                  brands: item.brands,
                                  stores: item.stores,
                                  monthly_price: item.monthly_price || '',
                                  yearly_price: item.yearly_price || '',
                                  credits: item.credits || item.requested_credits || '',
                                  yearly_credits: item.yearly_credits ?? '',
                                  max_users: item.max_users != null && item.max_users !== -1 ? item.max_users : (item.requested_users || ''),
                                  yearly_max_users: item.yearly_max_users ?? '',
                                  features: item.features || [],
                                  yearly_features: item.yearly_features || [],
                                  feature_rows: seedFeatureRows({
                                    ...item,
                                    credits: item.credits || item.requested_credits || 0,
                                    max_users: item.max_users != null && item.max_users !== -1 ? item.max_users : (item.requested_users || 0),
                                  }),
                                }); }}>Set quote</button>
                              )}
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan="5">No customize requests yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {viewing && (
                    <div className="card border mb-4 text-start">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <strong>Customize request for {viewing.company_name}</strong>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setViewing(null)}>Close</button>
                      </div>
                      <div className="card-body">
                        <p className="mb-1"><strong>Email:</strong> {viewing.email || '-'}</p>
                        <p className="mb-1"><strong>Contact:</strong> {viewing.contact_name || '-'} · {viewing.phone || '-'}</p>
                        <p className="mb-1"><strong>Status:</strong> {viewing.status}{viewing.subscription_plan ? ` · Account plan: ${viewing.subscription_plan}` : ''}</p>
                        <p className="mb-1"><strong>Paid duration:</strong> {viewing.status === 'PAID' ? (viewing.paid_cycle === 'yearly' ? 'Yearly' : viewing.paid_cycle === 'monthly' ? 'Monthly' : 'Paid') : 'Not paid yet'}</p>
                        {viewing.paid_at && <p className="mb-1"><strong>Paid on:</strong> {new Date(viewing.paid_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>}
                        <p className="mb-1"><strong>Brands:</strong> {viewing.brands || '-'} · <strong>Stores:</strong> {viewing.stores || '-'}</p>
                        {viewing.question && <p className="mb-1"><strong>Question:</strong> {viewing.question}</p>}
                        {viewing.message && <p className="mb-3"><strong>Message:</strong> {viewing.message}</p>}
                        <div className="row">
                          <div className="col-md-6">
                            <div className="border rounded p-3 h-100">
                              <div className="fw-bold mb-2">Monthly {viewing.paid_cycle === 'monthly' ? '(this is what they paid)' : ''}</div>
                              <div>${Number(viewing.monthly_price || 0)} + VAT</div>
                              <div>{viewing.credits || 0} credits · {viewing.max_users === -1 ? 'Unlimited' : (viewing.max_users || 0)} users</div>
                              <ul className="mb-0 mt-2">{(viewing.features || []).map((feature) => <li key={feature}>{feature}</li>)}</ul>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="border rounded p-3 h-100">
                              <div className="fw-bold mb-2">Yearly {viewing.paid_cycle === 'yearly' ? '(this is what they paid)' : ''}</div>
                              <div>${Number(viewing.yearly_price || 0)} + VAT</div>
                              <div>{(viewing.yearly_credits ?? viewing.credits) || 0} credits · {viewing.yearly_max_users == null ? (viewing.max_users === -1 ? 'Unlimited' : (viewing.max_users || 0)) : viewing.yearly_max_users} users</div>
                              <ul className="mb-0 mt-2">{((viewing.yearly_features && viewing.yearly_features.length) ? viewing.yearly_features : (viewing.features || [])).map((feature) => <li key={`y-${feature}`}>{feature}</li>)}</ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {quote && (
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      setLoader(true);
                      PlanService.saveCustomQuote({ ...quote, ...packFeatureRows(quote.feature_rows) }).then((res) => {
                        setLoader(false);
                        if (res.status) {
                          setQuote(null);
                          fetchData();
                        } else {
                          window.alert(res.message || 'Could not save the quote');
                        }
                      }).catch(() => {
                        setLoader(false);
                        window.alert('Could not save the quote');
                      });
                    }}>
                      <h5 className="mb-3">Quote for {quote.company_name}</h5>
                      <p className="text-muted small">{[quote.brands && `Brands: ${quote.brands}`, quote.stores && `Stores: ${quote.stores}`, quote.message].filter(Boolean).join(' · ')}</p>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="card border mb-3">
                            <div className="card-header bg-light fw-bold">Monthly</div>
                            <div className="card-body">
                              <div className="mb-3">
                                <label className="form-label">Price</label>
                                <div className="input-group">
                                  <span className="input-group-text">$</span>
                                  <input className="form-control" type="number" value={quote.monthly_price} onChange={(e) => setQuote({ ...quote, monthly_price: e.target.value })} />
                                </div>
                              </div>
                              <div className="text-muted small">Credits, users, and the other features are set in the list below.</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="card border mb-3">
                            <div className="card-header bg-light fw-bold">Yearly</div>
                            <div className="card-body">
                              <div className="mb-3">
                                <label className="form-label">Price</label>
                                <div className="input-group">
                                  <span className="input-group-text">$</span>
                                  <input className="form-control" type="number" value={quote.yearly_price} onChange={(e) => setQuote({ ...quote, yearly_price: e.target.value })} />
                                </div>
                              </div>
                              <div className="text-muted small">Yearly price is the amount charged for a year. Yearly feature values are entered with each feature below.</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-bold">Features for this account</label>
                        <FeatureRows rows={quote.feature_rows || []} onChange={(feature_rows) => setQuote({ ...quote, feature_rows })} />
                      </div>
                      <button className="btn btn-primary me-2" type="submit">Save quote</button>
                      <button className="btn btn-outline-danger me-2" type="button" onClick={() => {
                        setLoader(true);
                        PlanService.saveCustomQuote({ ...quote, status: 'REJECTED' }).then(() => { setQuote(null); fetchData(); });
                      }}>Reject</button>
                      <button className="btn btn-outline-secondary" type="button" onClick={() => setQuote(null)}>Cancel</button>
                    </form>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
        <Loader isShow={loader} />
      </div>
    </div>
  );
};
